import type { FetchCartResponse } from '@eliadmin/core/src/api/cart/cart-api';
import {
  ShipmentType,
  type CartPayment,
  type CartShipment,
} from '@eliadmin/core/src/api/cart/cart-types';
import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { htmlToPlainText } from '@eliadmin/core/src/utils/html-utils';

export type Cart = FetchCartResponse['data'];

const PLURAL = new Intl.PluralRules('sk');

/** „1 produkt“, „3 produkty“, „5 produktov“. */
export function productCount(count: number, t: TranslateFn) {
  const form = PLURAL.select(count);
  return `${count} ${form === 'one' ? 'produkt' : form === 'few' ? 'produkty' : t('text173')}`;
}

/** The chosen shipment's free-delivery amount, before a choice the lowest one of the store. */
export function freeShippingThreshold(shipments: CartShipment[], shipmentId?: number) {
  const selected = shipments.find(shipment => String(shipment.id) === String(shipmentId));
  const amounts = (selected ? [selected] : shipments)
    .map(shipment => shipment.free_shipping ?? 0)
    .filter(amount => amount > 0);
  return amounts.length ? Math.min(...amounts) : null;
}

/** Savings of the applied promotions („Ušetríte na akciách“) – already part of every cart total. */
export function promotionSavings(cart: Cart) {
  return (cart.promotions ?? []).reduce((sum, promotion) => {
    const amount = Number(promotion.discount);
    return Number.isFinite(amount) && amount > 0 ? sum + amount : sum;
  }, 0);
}

/** VAT in the total („z toho DPH“). */
export function vatAmount(cart: Cart) {
  return Math.max(0, cart.total - cart.total_without_vat);
}

/** Pickup point / place still to choose for the shipment (core `CartSummaryCard.isShipmentFulfilled`). */
export function isShipmentReady(shipment: CartShipment | undefined) {
  if (!shipment || shipment.complete === false || shipment.settings?.complete === false) return false;
  const settings = shipment.settings;

  switch (shipment.type) {
    case ShipmentType.CUSTOM_PLACE:
      return (
        !!settings?.place_of_delivery &&
        (!shipment.enable_time_of_delivery || !!settings.time_of_delivery)
      );
    case ShipmentType.PACKETA:
    case ShipmentType.SPS_ZBOX:
    case ShipmentType.GLS_ZBOX:
      return !!settings?.branch_id;
    case ShipmentType.TOP_TRANS:
      return shipment.price_type !== 'dynamic-api' || (!!settings?.city && !!settings.postcode);
    default:
      return true;
  }
}

/** Types whose shipment needs a pickup point chosen in a picker. */
export const PICKUP_TYPES: readonly string[] = [
  ShipmentType.PACKETA,
  ShipmentType.SPS_ZBOX,
  ShipmentType.GLS_ZBOX,
];

/** The administration description, unless it only repeats the name (as the payments do now). */
export function methodDescription(
  method: Pick<CartShipment | CartPayment, 'name' | 'description'>,
) {
  const text = htmlToPlainText(method.description ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return text && text.toLocaleLowerCase('sk') !== method.name.trim().toLocaleLowerCase('sk')
    ? text
    : '';
}

/** Online payment that redirects to (or embeds) a gateway after the order is sent. */
export function isGatewayPayment(payment: Pick<CartPayment, 'gateway'> | null | undefined) {
  return !!payment?.gateway;
}
